/**
 * StudioPatternThumb.tsx — Prévia fiel dos padrões do Studio
 *
 * A miniatura usa os mesmos componentes do canvas e da publicação. Assim, o
 * usuário escolhe pelo resultado real, sem uma representação esquemática que
 * possa divergir do bloco inserido.
 *
 * Como funciona a escala (v3.0.0): o modelo é sempre desenhado numa LARGURA
 * VIRTUAL fixa (1180px, a mesma proporção de um monitor) e depois reduzido para
 * caber no cartão. Antes a escala era um número fixo, então cartões maiores ou
 * menores cortavam o conteúdo e o texto parecia estourar a caixa. Agora a
 * redução é medida em tempo real, e o desenho fica idêntico ao da página — só
 * que menor.
 *
 * RELEASE: v3.0.0 - 2026-09-11 - Largura virtual fixa com escala medida e raiz
 *          `studio-doc`, para a miniatura respeitar as regras responsivas.
 * RELEASE: v2.1.0 - 2026-09-10 - Altura aumentada e borda removida; sombra fica
 *          por conta do cartão da galeria.
 * RELEASE: v2.0.0 - 2026-09-10 - Miniaturas reais pelo registro web oficial.
 */
import React from 'react';
import { getWebBlock } from '@/lib/studio/render/web/webBlockRegistry';
import { StudioWebProvider } from '@/lib/studio/render/web/StudioWebContext';
import type { StudioPatternNode } from '@/lib/studio/core/patterns/types';
import type { StudioBlockNode, StudioDocument } from '@/lib/studio/core';
import type { PublicProposalData } from '@/hooks/usePublicProposal';

interface StudioPatternThumbProps {
  nodes?: StudioPatternNode[];
}

/** Largura virtual em que o modelo é desenhado antes de ser reduzido. */
const LARGURA_VIRTUAL = 1180;
/** Altura visível do cartão da miniatura, em pixels. */
const ALTURA_CARTAO = 240;

/** Dados mínimos para renderizar a prévia comercial. */
const MOCK_DATA: PublicProposalData = {
  proposal: {
    id: 'thumb',
    proposal_number: 'PRP-0001',
    version_number: 1,
    title: 'Proposta Exemplo',
    description: 'Uma solução sob medida para o seu negócio.',
    conditions_html: '<p>Condições comerciais de exemplo.</p>',
    subtotal: 1000,
    discount_total: 0,
    total: 1000,
    mrr_total: 0,
    currency: 'BRL',
    status: 'sent',
    valid_from: new Date().toISOString(),
    expires_at: null,
    accepted_at: null,
    accepted_by_name: null,
    rejected_at: null,
    pdf_status: 'ready',
    is_actionable: true,
  },
  tenant: { name: 'Empresa', logo_url: null },
  deal: { title: 'Negócio', client_name: 'Cliente' },
  items: [],
  pdf_url: null,
};

/** Documento mínimo válido exigido pelo contexto oficial de renderização. */
const DOCUMENTO_PREVIA: StudioDocument = {
  schemaVersion: 1,
  blocks: [],
  settings: {},
};

/** Converte o nó portátil do padrão para o contrato do renderizador. */
function materializarNo(node: StudioPatternNode, caminho: string): StudioBlockNode {
  return {
    ...node,
    id: `preview-${caminho}`,
    children: node.children?.map((filho, indice) => materializarNo(filho, `${caminho}-${indice}`)),
  };
}

function PreviewBlock({ node }: { node: StudioBlockNode }) {
  const Componente = getWebBlock(node.type);
  if (!Componente) {
    return <div className="h-20 rounded-md border border-dashed bg-muted/30" />;
  }
  const filhos = node.children?.map((filho) => (
    <PreviewBlock key={filho.id} node={filho} />
  ));
  return <Componente node={node}>{filhos}</Componente>;
}

export function StudioPatternThumb({ nodes }: StudioPatternThumbProps) {
  const caixaRef = React.useRef<HTMLDivElement | null>(null);
  const [escala, setEscala] = React.useState(0.3);

  // Mede a largura real do cartão e calcula a redução necessária.
  React.useEffect(() => {
    const elemento = caixaRef.current;
    if (!elemento) return;
    const medir = () => {
      const largura = elemento.clientWidth;
      if (largura > 0) setEscala(largura / LARGURA_VIRTUAL);
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(elemento);
    return () => observador.disconnect();
  }, []);

  if (!nodes || nodes.length === 0) {
    return (
      <div className="h-24 w-full overflow-hidden rounded-md border bg-muted/20 flex items-center justify-center">
        <div className="h-1.5 w-12 rounded-full bg-muted-foreground/30" />
      </div>
    );
  }

  const nosMaterializados = nodes.map((node, indice) => materializarNo(node, String(indice)));

  return (
    <div
      ref={caixaRef}
      className="relative w-full overflow-hidden rounded-lg bg-studio-paper transition-shadow group-hover:shadow-md"
      style={{ height: ALTURA_CARTAO }}
    >
      <div className="pointer-events-none absolute inset-0 select-none overflow-hidden">
        <div
          className="origin-top-left"
          style={{
            width: LARGURA_VIRTUAL,
            transform: `scale(${escala})`,
            // Garante que o recorte mostre sempre a mesma altura de documento.
            height: ALTURA_CARTAO / (escala || 1),
          }}
        >
          <StudioWebProvider value={{ proposal: MOCK_DATA, document: DOCUMENTO_PREVIA, actions: {} }}>
            <div
              // `st-no-motion`: na miniatura nada anima. Sem isto, animações que
              // começam invisíveis deixam o cartão branco com o texto apagado.
              className="studio-doc st-no-motion min-h-full overflow-hidden bg-studio-paper text-studio-paper-foreground"
              data-studio-render-mode="thumbnail"
            >
              {nosMaterializados.map((node) => (
                <PreviewBlock key={node.id} node={node} />
              ))}
            </div>
          </StudioWebProvider>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-studio-paper to-transparent" />
    </div>
  );
}
